import { styled } from '@linaria/react';
import { Icon } from '../../../../../ui-library/icon/icon';
import { SaveState } from './settings.type';

interface SaveStatusProps {
  state: SaveState;
}

const STATUS_COPY: Record<Exclude<SaveState, SaveState.Idle>, { icon: string; label: string; color: string }> = {
  [SaveState.Saving]: { icon: 'sync', label: 'Saving…', color: 'var(--ink-light)' },
  [SaveState.Saved]: { icon: 'cloud_done', label: 'Saved', color: 'var(--primary-darkest)' },
  [SaveState.Error]: { icon: 'error_outline', label: "Couldn't save", color: 'var(--red-darkest)' },
};

/**
 * Renders the inline "Saving… / Saved / Couldn't save" note for an autosaving
 * settings page. Announced politely to screen readers; renders an empty live
 * region while idle so the first change is announced too.
 *
 * @param {SaveStatusProps} props - The current save state
 * @returns {JSX.Element} The status note
 *
 * @example
 * <SaveStatus state={saveState} />
 */
export function SaveStatus({ state }: SaveStatusProps) {
  const copy = state === SaveState.Idle ? null : STATUS_COPY[state];

  return (
    <Status role="status" aria-live="polite" data-state={state}>
      {copy && (
        <>
          {/* The icon font renders its ligature name as text; hide it from screen readers. */}
          <IconSlot aria-hidden="true">
            <Icon name={copy.icon} size={16} color={copy.color} />
          </IconSlot>
          <span>{copy.label}</span>
        </>
      )}
    </Status>
  );
}

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 20px;
  font-size: var(--font-size-small);
  color: var(--ink-light);

  &[data-state='error'] {
    color: var(--red-darkest);
  }
`;

const IconSlot = styled.span`
  display: inline-flex;
`;

export type { SaveStatusProps };
